import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader, Mic, MicOff, Copy, Check, Volume2, Square } from 'lucide-react';
import { G1, SS } from '../../config/styles';
import { supabase, apiUrl } from '../../config/supabase';


// ── زر نسخ صغير (لأي نص / كود) ─────────────────────────────
function CopyBtn({ text, dark }) {
  const [ok, setOk] = useState(false);
  const doCopy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(text); }
    catch { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); }
    setOk(true); setTimeout(() => setOk(false), 1500);
  };
  return (
    <button onClick={doCopy} title="نسخ"
      style={{ background: dark ? 'rgba(255,255,255,0.12)' : '#F3F4F6', border: 'none', borderRadius: '6px', padding: '3px 6px', cursor: 'pointer', color: dark ? '#e5e7eb' : '#4B5563', display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}>
      {ok ? <Check size={11} /> : <Copy size={11} />}{ok ? 'اتنسخ' : 'نسخ'}
    </button>
  );
}

// ── عرض رد البوت: code blocks (بزر نسخ) + **bold** + لينكات + أسطر ──
function RichText({ text, isUser }) {
  const parts = String(text || '').split(/```([a-zA-Z0-9_-]*)\n?([\s\S]*?)```/g);
  const out = [];
  for (let i = 0; i < parts.length; i += 3) {
    const plain = parts[i];
    if (plain) out.push(<span key={`p${i}`}>{renderInline(plain, isUser)}</span>);
    if (i + 2 < parts.length) {
      const code = parts[i + 2] || '';
      out.push(
        <div key={`c${i}`} style={{ position: 'relative', margin: '6px 0', background: '#0f172a', color: '#e2e8f0', borderRadius: '8px', padding: '26px 10px 10px', direction: 'ltr', textAlign: 'left', fontFamily: 'Consolas, monospace', fontSize: '11px', overflowX: 'auto', whiteSpace: 'pre' }}>
          <div style={{ position: 'absolute', top: '5px', right: '6px' }}><CopyBtn text={code.trim()} dark /></div>
          {code.trim()}
        </div>
      );
    }
  }
  return <>{out}</>;
}
function renderInline(text, isUser) {
  // links + bold
  return text.split(/(https?:\/\/[^\s)]+|\*\*[^*\n]+\*\*)/g).map((part, idx) => {
    if (/^https?:\/\/(wa\.me|api\.whatsapp\.com|whatsapp\.com)/i.test(part)) {
      return (
        <a key={idx} href={part} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#25D366', color: 'white', padding: '5px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 'bold', textDecoration: 'none', margin: '3px 0' }}>
          💬 كلمنا على واتساب
        </a>
      );
    }
    if (/^https?:\/\//.test(part)) return <a key={idx} href={part} target="_blank" rel="noopener noreferrer" style={{ color: isUser ? '#c7d2fe' : '#667eea', textDecoration: 'underline', wordBreak: 'break-all' }}>{part}</a>;
    if (/^\*\*[^*\n]+\*\*$/.test(part)) return <b key={idx}>{part.slice(2, -2)}</b>;
    return part;
  });
}

// ── نطق الرد بالصوت (Web Speech — بيشتغل في كروم/إيدج/سفاري) ──
function speak(text, onEnd, rate = 1) {
  try {
    if (!('speechSynthesis' in window)) return false;
    window.speechSynthesis.cancel();
    const clean = String(text).replace(/```[\s\S]*?```/g, ' ').replace(/[*_#`>]/g, '').replace(/https?:\/\/\S+/g, ' لينك ').replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '');
    const u = new SpeechSynthesisUtterance(clean.slice(0, 1500));
    u.lang = 'ar-EG';
    const voices = window.speechSynthesis.getVoices();
    const v = voices.find(x => /ar-EG/i.test(x.lang)) || voices.find(x => /^ar/i.test(x.lang));
    if (v) u.voice = v;
    u.rate = rate; u.onend = onEnd; u.onerror = onEnd;
    window.speechSynthesis.speak(u);
    return true;
  } catch { return false; }
}

// ── فقاعة صوت (زي واتساب): زر تشغيل + موجات + "عرض النص" ──
function VoiceBubble({ m, isUser, playing, onToggle, speed = 1, onSpeed }) {
  const [showText, setShowText] = useState(false);
  const bars = [4, 9, 6, 12, 8, 14, 7, 11, 5, 13, 8, 10, 6, 12, 9, 7, 11, 5, 9, 6];
  const words = String(m.content || '').replace(/```[\s\S]*?```/g, '').split(/\s+/).filter(Boolean).length;
  const secs = Math.max(2, Math.round(words / 2.5));
  const dur = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  const fg = isUser ? 'white' : '#4c51bf';
  return (
    <div style={{ minWidth: '190px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button onClick={onToggle}
          style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', cursor: 'pointer', background: isUser ? 'rgba(255,255,255,0.25)' : '#EEF2FF', color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {playing ? <Square size={13} /> : <span style={{ fontSize: '13px', marginInlineStart: '2px' }}>▶</span>}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', height: '22px', flex: 1 }}>
          {bars.map((h, i) => (
            <span key={i} style={{ width: '3px', height: `${h}px`, borderRadius: '2px', background: fg, opacity: playing ? 1 : 0.55, animation: playing ? `wave 0.9s ease-in-out ${i * 0.05}s infinite` : 'none' }} />
          ))}
        </div>
        <span style={{ fontSize: '10px', color: isUser ? 'rgba(255,255,255,0.85)' : '#6B7280' }}>{m.duration || dur}</span>
        {/* ⏩ سرعة التشغيل زي واتساب: 1x → 1.5x → 2x */}
        <button onClick={(e) => { e.stopPropagation(); onSpeed && onSpeed(); }} title="سرعة التشغيل"
          style={{ border: 'none', cursor: 'pointer', borderRadius: '999px', padding: '2px 7px', fontSize: '10px', fontWeight: 'bold', background: isUser ? 'rgba(255,255,255,0.22)' : '#EEF2FF', color: fg, minWidth: '34px' }}>
          {speed}x
        </button>
      </div>
      {m.audioUrl && <audio src={m.audioUrl} preload="metadata" style={{ display: 'none' }} />}
      <button onClick={() => setShowText(v => !v)}
        style={{ marginTop: '4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', color: isUser ? 'rgba(255,255,255,0.85)' : '#6B7280', padding: 0, textDecoration: 'underline' }}>
        {showText ? 'إخفاء النص' : 'عرض النص'}
      </button>
      {showText && (
        <div style={{ marginTop: '4px', fontSize: '12px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
          <RichText text={m.content} isUser={isUser} />
        </div>
      )}
    </div>
  );
}

// ── 🎬 مرفق (فيديو/صورة) بيتفتح جوه الشات زي واتساب — مش لينك خارجي ──
function MediaCard({ a }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  const isFrame = a.kind === 'vimeo' || a.kind === 'youtube' || a.kind === 'drive';
  const box = { position: 'relative', width: '100%', aspectRatio: '16/9', background: '#0f172a', borderRadius: '10px', overflow: 'hidden' };
  const Player = ({ big }) => isFrame
    ? <iframe src={a.src + (a.kind === 'vimeo' ? '&autoplay=1' : '&autoplay=1')} title={a.title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
    : a.kind === 'video' ? <video src={a.src} controls autoPlay playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: '#000' }} />
    : <img src={a.src} alt={a.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />;
  return (
    <>
      <div style={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden', background: 'white', marginTop: '6px' }}>
        <div style={box}>
          {open ? <Player /> : (
            <button onClick={() => setOpen(true)} style={{ position: 'absolute', inset: 0, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#1e1b4b,#312e81)', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              {a.kind === 'image'
                ? <img src={a.src} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }} />
                : null}
              <span style={{ position: 'relative', width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255,255,255,0.92)', color: '#4c51bf', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }}>▶</span>
              <span style={{ position: 'relative', fontSize: '11px', opacity: 0.9 }}>{a.kind === 'image' ? 'اضغط للعرض' : 'اضغط للتشغيل'}</span>
            </button>
          )}
          {open && (
            <button onClick={() => setFull(true)} title="تكبير" style={{ position: 'absolute', top: '6px', left: '6px', zIndex: 2, background: 'rgba(0,0,0,0.55)', color: 'white', border: 'none', borderRadius: '6px', padding: '3px 7px', fontSize: '11px', cursor: 'pointer' }}>⛶</button>
          )}
        </div>
        <div style={{ padding: '6px 10px', fontSize: '11px', color: '#374151', display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
          <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
          <span style={{ color: '#6B7280', whiteSpace: 'nowrap' }}>{a.student}{a.course ? ` · ${a.course}` : ''}</span>
        </div>
      </div>
      {full && (
        <div onClick={() => setFull(false)} style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(96vw, 960px)', aspectRatio: '16/9', position: 'relative', background: '#000', borderRadius: '12px', overflow: 'hidden' }}>
            <Player big />
          </div>
          <button onClick={() => setFull(false)} style={{ position: 'fixed', top: '14px', left: '14px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', fontSize: '18px', cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </>
  );
}

// مفتاح زائر ثابت في المتصفح — عشان عدّاد التجربة المجانية (السيرفر بيحسبه)
const getVisitorKey = () => {
  try {
    let k = localStorage.getItem('bot_visitor');
    if (!k) { k = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('bot_visitor', k); }
    return k;
  } catch { return ''; }
};

export default function FloatingButtons({ settings, isRTL, lang, courses, services, t, customer, onOpenCustModal, onSetPage }) {
  const [showAiChat, setShowAiChat] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput,    setAiInput]    = useState('');
  const [aiLoading,  setAiLoading]  = useState(false);
  const [botInfo,    setBotInfo]    = useState({ bot_name: '', welcome_message: '' });
  const [voiceCfg,   setVoiceCfg]   = useState({ tts_provider: 'browser', stt_provider: 'browser' }); // من الداشبورد
  const [me,         setMe]         = useState(null);   // حالة العميل مع البوت (أيام متبقية + رسالة التفعيل)
  const [waLink,     setWaLink]     = useState(() => { const n = String(settings?.whatsapp_number || '').replace(/\D/g, ''); return n ? `https://wa.me/${n}` : ''; });
  const ttsAudioRef = useRef(null);   // تشغيل صوت السيرفر (Gemini/ElevenLabs/OpenAI)
  const [speed, setSpeed] = useState(() => { try { return Number(localStorage.getItem('bot_voice_speed')) || 1; } catch { return 1; } });
  const speedRef = useRef(1);
  useEffect(() => { speedRef.current = speed; try { localStorage.setItem('bot_voice_speed', String(speed)); } catch {}
    // طبّق على أي صوت شغال دلوقتي
    try { if (ttsAudioRef.current) ttsAudioRef.current.playbackRate = speed; } catch {}
    try { if (audioElRef.current) audioElRef.current.playbackRate = speed; } catch {}
  }, [speed]);
  const cycleSpeed = () => setSpeed(v => v >= 2 ? 1 : v >= 1.5 ? 2 : 1.5);
  const [actions,    setActions]    = useState([]);      // أزرار حسب حالة العميل (login / enroll / renew)
  const [listening,  setListening]  = useState(false);   // 🎤 تسجيل صوت
  const [micPerm,    setMicPerm]    = useState(false);   // ⏳ مستنيين إذن الميكروفون من المتصفح
  const [speakingIdx, setSpeakingIdx] = useState(null);  // 🔊 الرسالة اللي بتتقري
  const [voiceReply, setVoiceReply] = useState(false);   // لو السؤال جه بالصوت → الرد يتقري تلقائي
  const recogRef = useRef(null);
  const mediaRef = useRef(null);      // MediaRecorder لتسجيل الصوت الفعلي
  const chunksRef = useRef([]);
  const audioElRef = useRef(null);    // تشغيل صوت العميل المسجّل
  const recStartRef = useRef(0);
  const messagesEndRef = useRef(null);

  const openWA = () =>
    window.open(`https://wa.me/${settings.whatsapp_number?.replace(/\D/g, '')}?text=${encodeURIComponent(settings.whatsapp_message || '')}`, '_blank');

  // اسم البوت ورسالة الترحيب — من bot_settings (الداشبورد بتتحكم فيهم)
  useEffect(() => {
    supabase.from('bot_settings')
      .select('bot_name, welcome_message, is_enabled')
      .eq('id', 1)
      .then(({ data }) => { if (data && data[0]) setBotInfo(data[0]); });
    fetch(apiUrl('/api/bot/voice-config')).then(r => r.json()).then(c => { if (c && c.tts_provider) setVoiceCfg(c); }).catch(() => {});
  }, []);

  // اسكرول لآخر رسالة
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages, aiLoading]);

  // ⚠️ الـ system prompt بقى بيتبني على السيرفر (api/bot.js) من bot_settings +
  //    الكورسات + ai_knowledge + أعمال الطلبة — نفس العقل اللي بيخدم تليجرام.
  //    الكلاينت بيبعت الرسالة والتاريخ وتوكن العميل بس.

  const sendAi = async (forcedText, fromVoice = false, meta = {}) => {
    const msg = (typeof forcedText === 'string' ? forcedText : aiInput).trim();
    if (!msg || aiLoading) return;
    setVoiceReply(fromVoice);

    const userMsg = { role: 'user', content: msg, isVoice: fromVoice, ...meta };
    setAiMessages(p => [...p, userMsg]);
    setAiInput('');
    setAiLoading(true);
    setActions([]);

    try {
      const history = aiMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      let token = '';
      try { token = localStorage.getItem('cust_token') || ''; } catch {}

      // ✅ العقل الواحد: /api/bot/chat — الحارس + البرومبت + Claude كله سيرفر سايد
      const res = await fetch(apiUrl('/api/bot/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: 'web',
          message: msg,
          history,
          token: token || undefined,
          visitor_key: getVisitorKey(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      const reply = (res.ok && data?.reply)
        ? data.reply
        : (data?.message || (lang === 'ar' ? 'عذراً، حدث خطأ. حاول مرة أخرى!' : 'Sorry, an error occurred!'));
      setAiMessages(p => {
        const next = [...p, { role: 'assistant', content: reply, isVoice: fromVoice, attachments: Array.isArray(data?.attachments) ? data.attachments : [] }];
        if (fromVoice && res.ok) { const idx = next.length - 1; setSpeakingIdx(idx); speakText(reply, () => setSpeakingIdx(null)); }
        return next;
      });
      if (Array.isArray(data?.actions)) setActions(data.actions);
      if (data?.whatsapp?.url) setWaLink(data.whatsapp.url);
    } catch {
      setAiMessages(p => [...p, { role: 'assistant', content: lang === 'ar' ? '⚠️ مش قادر أتواصل دلوقتي، جرب تاني!' : '⚠️ Connection error, try again!' }]);
    }

    setAiLoading(false);
  };

  // تنفيذ زر من أزرار الحالة
  const runAction = (a) => {
    if (a.type === 'login')  { onOpenCustModal && onOpenCustModal('login'); return; }
    if (a.type === 'renew') { onSetPage && onSetPage('bot-subscribe'); setShowAiChat(false); return; }
    if (a.type === 'enroll') { onSetPage && onSetPage('academy'); setShowAiChat(false); return; }
    if (a.url) window.open(a.url, '_blank');
  };

  // 🎤 تسجيل صوت — مسارين حسب الداشبورد:
  //   stt_provider = browser → Web Speech (كروم/إيدج) + تسجيل للفقاعة
  //   stt_provider = gemini/openai → تسجيل بس، والتفريغ على السيرفر (/api/bot/stt) — بيشتغل في كل المتصفحات
  const blobToB64 = (blob) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(',')[1]); r.onerror = reject; r.readAsDataURL(blob); });
  const fmtDur = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  const startRecorder = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mr = new MediaRecorder(stream);
    chunksRef.current = [];
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    const done = new Promise(resolve => {
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        resolve(blob.size ? blob : null);
      };
    });
    mr.start();
    mediaRef.current = mr;
    recStartRef.current = Date.now();
    // ⚠️ بنرجّع object مش الـ promise نفسه — عشان await startRecorder() ميستناش لحد ما التسجيل يقف
    return { done };
  };

  const toggleMic = async () => {
    try { return await toggleMicInner(); }
    catch (e) {
      console.error('[bot-mic]', e);
      setListening(false);
      setAiMessages(p => [...p, { role: 'assistant', content: `⚠️ مشكلة في الميكروفون: ${e?.name || ''} ${e?.message || e}` }]);
    }
  };
  const toggleMicInner = async () => {
    console.log('[bot-mic] click — listening:', listening, 'cfg:', voiceCfg);
    if (listening) { try { recogRef.current?.stop(); } catch {} try { mediaRef.current?.state === 'recording' && mediaRef.current.stop(); } catch {} setListening(false); return; }
    // لو في أي مزوّد سيرفر (Gemini/OpenAI) في السلسلة → نستخدم السيرفر (أضمن — بيشتغل في كل المتصفحات)
    const useServerStt = (voiceCfg.stt_order || [voiceCfg.stt_provider]).some(p => p && p !== 'browser');
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!useServerStt && !SR) { setAiMessages(p => [...p, { role: 'assistant', content: 'المتصفح ده مش بيدعم الميكروفون — جرّب كروم أو إيدج، أو خلّي الإدارة تفعّل التفريغ من السيرفر 🙏' }]); return; }

    // ── مسار السيرفر: سجّل → ابعت للسيرفر يفرّغ ──
    if (useServerStt) {
      let done;
      setMicPerm(true);
      try { ({ done } = await startRecorder()); console.log('[bot-mic] recorder started'); }
      catch (e) { setMicPerm(false); console.error('[bot-mic] getUserMedia/MediaRecorder failed', e); setAiMessages(p => [...p, { role: 'assistant', content: `مش قادر أوصل للميكروفون (${e?.name || 'خطأ'}) — اسمح للموقع باستخدامه من إعدادات المتصفح 🎤` }]); return; }
      setMicPerm(false);
      setListening(true);
      recogRef.current = { stop: () => { try { mediaRef.current?.state === 'recording' && mediaRef.current.stop(); } catch {} } };
      // لما يوقف التسجيل (بالزر) — نكمل
      const blob = await done;
      console.log('[bot-mic] recorder stopped, blob:', blob && blob.size, blob && blob.type);
      setListening(false);
      const secs = Math.max(1, Math.round((Date.now() - recStartRef.current) / 1000));
      if (!blob) return;
      const audioUrl = URL.createObjectURL(blob);
      setAiLoading(true);
      try {
        const audio_b64 = await blobToB64(blob);
        const r = await fetch(apiUrl('/api/bot/stt'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audio_b64, mime: blob.type }) });
        const j = res204(r) ? {} : await r.json().catch(() => ({}));
        console.log('[bot-mic] stt response', r.status, j);
        setAiLoading(false);
        const txt = (j.text || '').trim();
        if (r.ok && txt) sendAi(txt, true, { audioUrl, duration: fmtDur(secs) });
        else if (r.status === 502 && j.tried) setAiMessages(p => [...p, { role: 'assistant', content: `⚠️ خدمة التفريغ مش شغالة دلوقتي — بلّغ الإدارة.\n(${j.tried.join(' | ').slice(0, 300)})` }]);
        else setAiMessages(p => [...p, { role: 'assistant', content: 'مقدرتش أفهم الصوت 🙏 جرّب تاني وقرّب من الميك.' }]);
      } catch { setAiLoading(false); setAiMessages(p => [...p, { role: 'assistant', content: '⚠️ مش قادر أتواصل دلوقتي، جرب تاني!' }]); }
      return;
    }

    // ── مسار المتصفح: Web Speech للنص + تسجيل للفقاعة ──
    let audioDone = Promise.resolve(null);
    let srAlive = true;
    try {
      audioDone = startRecorder().then(({ done }) => done).then(b => b ? URL.createObjectURL(b) : null);
      // لو الـ SR وقع قبل ما الميك يفتح — اقفل الميك أول ما يفتح
      Promise.resolve().then(async () => { await new Promise(r => setTimeout(r, 300)); if (!srAlive) { try { mediaRef.current?.state === 'recording' && mediaRef.current.stop(); } catch {} } });
    } catch { mediaRef.current = null; }
    recStartRef.current = Date.now();
    const r = new SR();
    r.lang = 'ar-EG'; r.interimResults = true; r.continuous = false; r.maxAlternatives = 1;
    let finalText = '';
    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript; else interim += e.results[i][0].transcript;
      }
      setAiInput(finalText || interim);
    };
    r.onerror = (e) => {
      srAlive = false;
      setListening(false);
      try { mediaRef.current?.state === 'recording' && mediaRef.current.stop(); } catch {}
      const code = e?.error || '';
      const msg = code === 'not-allowed' || code === 'service-not-allowed' ? 'المتصفح مانع الميكروفون — اسمح للموقع باستخدامه من أيقونة القفل جنب العنوان 🎤'
        : code === 'network' ? 'خدمة التعرف على الصوت في المتصفح مش متاحة دلوقتي — خلّي الإدارة تفعّل التفريغ من السيرفر (Gemini) 🙏'
        : code === 'no-speech' ? 'مسمعتش حاجة 🙏 جرّب تاني وقرّب من الميك.'
        : code === 'aborted' ? '' : `مشكلة في الميكروفون (${code || 'غير معروفة'}) — جرّب تاني`;
      if (msg) setAiMessages(p => [...p, { role: 'assistant', content: msg }]);
    };
    r.onend = async () => {
      srAlive = false;
      setListening(false);
      try { mediaRef.current?.state === 'recording' && mediaRef.current.stop(); } catch {}
      const secs = Math.max(1, Math.round((Date.now() - recStartRef.current) / 1000));
      const audioUrl = await audioDone;
      const txt = finalText.trim();
      if (txt) { setAiInput(''); sendAi(txt, true, { audioUrl, duration: fmtDur(secs) }); }
      else setAiMessages(p => [...p, { role: 'assistant', content: 'مسمعتش حاجة واضحة 🙏 جرّب تاني وقرّب من الميك.' }]);
    };
    recogRef.current = r;
    try { r.start(); setListening(true); } catch { setListening(false); }
  };
  const res204 = (r) => r.status === 204;

  // تشغيل/إيقاف صوت رسالة العميل المسجّلة
  const [playingUserIdx, setPlayingUserIdx] = useState(null);
  const [recTick, setRecTick] = useState('0:00');
  useEffect(() => {
    if (!listening) { setRecTick('0:00'); return; }
    const id = setInterval(() => { const sec = Math.round((Date.now() - recStartRef.current) / 1000); setRecTick(`${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`); }, 500);
    return () => clearInterval(id);
  }, [listening]);
  const toggleUserAudio = (idx, url) => {
    try {
      if (playingUserIdx === idx) { audioElRef.current?.pause(); setPlayingUserIdx(null); return; }
      audioElRef.current?.pause();
      const a = new Audio(url); audioElRef.current = a;
      a.playbackRate = speedRef.current;
      a.onended = () => setPlayingUserIdx(null);
      a.play(); setPlayingUserIdx(idx);
    } catch { setPlayingUserIdx(null); }
  };

  // نطق موحّد: مزوّد السيرفر (من الداشبورد) → fallback صوت المتصفح
  const stopSpeaking = () => {
    try { window.speechSynthesis?.cancel(); } catch {}
    try { if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; } } catch {}
  };
  const speakText = async (text, onEnd) => {
    stopSpeaking();
    const serverTts = (voiceCfg.tts_order || [voiceCfg.tts_provider]).some(p => p && p !== 'browser');
    if (serverTts) {
      try {
        const res = await fetch(apiUrl('/api/bot/tts'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
        if (res.ok && res.status !== 204) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = new Audio(url); ttsAudioRef.current = a;
          a.playbackRate = speedRef.current;
          a.onended = () => { URL.revokeObjectURL(url); onEnd && onEnd(); };
          a.onerror = () => { onEnd && onEnd(); };
          await a.play();
          return;
        }
      } catch {}
    }
    if (!speak(text, onEnd, speedRef.current)) onEnd && onEnd();
  };
  const toggleSpeak = (idx, text) => {
    if (speakingIdx === idx) { stopSpeaking(); setSpeakingIdx(null); return; }
    setSpeakingIdx(idx); speakText(text, () => setSpeakingIdx(null));
  };
  useEffect(() => () => { stopSpeaking(); try { recogRef.current?.stop(); } catch {} }, []); // eslint-disable-line

  // 🎉 حالة العميل مع المساعد: أيام الدعم المتبقية + رسالة التفعيل (تظهر أول مرة في الجلسة)
  useEffect(() => {
    if (!showAiChat) return;
    let token = ''; try { token = localStorage.getItem('cust_token') || ''; } catch {}
    if (!token) { setMe(null); return; }
    fetch(apiUrl('/api/bot/me'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) })
      .then(r => r.json()).then(j => {
        if (!j || !j.ok) return;
        setMe(j);
        if (j.state === 'open' && j.activation_message) {
          const key = 'bot_activation_shown_' + (customer?.id || 'x');
          let shown = ''; try { shown = sessionStorage.getItem(key); } catch {}
          if (!shown) {
            setAiMessages(p => p.some(m => m.isActivation) ? p : [...p, { role: 'assistant', content: j.activation_message, isActivation: true }]);
            try { sessionStorage.setItem(key, '1'); } catch {}
          }
        }
      }).catch(() => {});
  }, [showAiChat, customer?.id]); // eslint-disable-line

  // لو العميل سجّل دخول والشات مفتوح — نظّف الأزرار عشان يكمل عادي
  useEffect(() => { if (customer && !customer.isGuest) setActions([]); }, [customer]);

  const side = isRTL ? 'left' : 'right';

  return (
    <>
      {/* AI Chat button */}
      <button onClick={() => setShowAiChat(x => !x)}
        style={{ position: 'fixed', bottom: '96px', [side]: '22px', width: '52px', height: '52px', borderRadius: '50%', background: G1, color: 'white', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(102,126,234,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 40, transition: 'transform .2s' }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
        {showAiChat ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* AI Chat panel */}
      {showAiChat && (
        <div style={{ position: 'fixed', bottom: '158px', [side]: '22px', width: '360px', maxWidth: '92vw', background: 'white', borderRadius: '18px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', zIndex: 40, overflow: 'hidden', animation: 'fadeInDown .2s ease' }}>

          {/* Header */}
          <div style={{ background: G1, padding: '14px 16px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🤖</div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{botInfo.bot_name || t.aiAssistant}</div>
                {me?.state === 'open' && (
                  <div style={{ fontSize: '10px', opacity: 0.9 }}>
                    🎁 اشتراك المساعد — باقي {me.days_left} يوم
                    {me.days_left <= 30 && <button onClick={() => { onSetPage && onSetPage('bot-subscribe'); setShowAiChat(false); }} style={{ marginInlineStart: 6, background: '#f97316', color: 'white', border: 'none', borderRadius: 999, padding: '1px 8px', fontSize: '10px', cursor: 'pointer', fontWeight: 'bold' }}>جدّد</button>}
                  </div>
                )}
                {me?.state === 'expired' && <div style={{ fontSize: '10px' }}><button onClick={() => { onSetPage && onSetPage('bot-subscribe'); setShowAiChat(false); }} style={{ background: '#f97316', color: 'white', border: 'none', borderRadius: 999, padding: '1px 8px', fontSize: '10px', cursor: 'pointer', fontWeight: 'bold' }}>⏰ اشتراكك انتهى — جدّد</button></div>}
                <div style={{ fontSize: '10px', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                  {lang === 'ar' ? 'متصل الآن' : 'Online'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {waLink && (
                <a href={waLink} target="_blank" rel="noopener noreferrer" title="كلمنا على واتساب"
                  style={{ background: '#25D366', color: 'white', borderRadius: '999px', padding: '4px 9px', fontSize: '11px', fontWeight: 'bold', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  💬 واتساب
                </a>
              )}
              <button onClick={() => setShowAiChat(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', opacity: 0.8 }}><X size={16} /></button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ height: '340px', overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', background: '#FAFAFA' }}>

            {/* Welcome message */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: G1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>🤖</div>
              <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '9px 12px', borderRadius: '12px 12px 12px 2px', fontSize: '12px', color: '#1F2937', maxWidth: '80%', lineHeight: '1.5', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                {botInfo.welcome_message || t.aiHi}
              </div>
            </div>

            {/* Chat messages */}
            {aiMessages.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
                {m.role === 'assistant' && (
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: G1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>🤖</div>
                )}
                <div style={{ maxWidth: '88%', flex: (m.attachments && m.attachments.length) ? 1 : 'initial', display: 'flex', flexDirection: 'column', gap: '3px', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ background: m.role === 'user' ? G1 : 'white', border: m.role === 'user' ? 'none' : '1px solid #E5E7EB', color: m.role === 'user' ? 'white' : '#1F2937', padding: '9px 12px', borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px', fontSize: '12px', lineHeight: '1.6', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {m.isVoice
                      ? <VoiceBubble m={m} isUser={m.role === 'user'} speed={speed} onSpeed={cycleSpeed}
                          playing={m.role === 'user' ? playingUserIdx === i : speakingIdx === i}
                          onToggle={() => m.role === 'user'
                            ? (m.audioUrl ? toggleUserAudio(i, m.audioUrl) : toggleSpeak(i, m.content))
                            : toggleSpeak(i, m.content)} />
                      : <RichText text={m.content} isUser={m.role === 'user'} />}
                  </div>
                  {/* 🎬 مرفقات (أعمال الطلبة) — بتتفتح جوه الشات */}
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div style={{ width: '100%', minWidth: '240px' }}>
                      {m.attachments.map(a => <MediaCard key={a.id || a.src} a={a} />)}
                    </div>
                  )}
                  {/* شريط أدوات الرسالة: نسخ + استماع */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <CopyBtn text={m.content} />
                    {m.role === 'assistant' && !m.isVoice && (
                      <button onClick={() => toggleSpeak(i, m.content)} title={speakingIdx === i ? 'إيقاف' : 'استمع'}
                        style={{ background: speakingIdx === i ? '#EEF2FF' : '#F3F4F6', border: 'none', borderRadius: '6px', padding: '3px 6px', cursor: 'pointer', color: speakingIdx === i ? '#4c51bf' : '#4B5563', display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}>
                        {speakingIdx === i ? <Square size={11} /> : <Volume2 size={11} />}{speakingIdx === i ? 'إيقاف' : 'استمع'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {aiLoading && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: G1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>🤖</div>
                <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '10px 14px', borderRadius: '12px 12px 12px 2px', display: 'flex', gap: '4px', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#667eea', animation: `bounce 1s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                  <span style={{ fontSize: '11px', color: '#6B7280', marginInlineStart: '6px' }}>{lang === 'ar' ? 'بيكتب…' : 'typing…'}</span>
                </div>
              </div>
            )}

            {/* أزرار الحالة: تسجيل دخول / اشتراك / تجديد */}
            {!aiLoading && actions.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', paddingInlineStart: '30px' }}>
                {actions.map((a, i) => (
                  <button key={i} onClick={() => runAction(a)}
                    style={{ padding: '6px 10px', background: 'white', border: '1.5px solid #667eea', color: '#4c51bf', borderRadius: '999px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {a.label}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* ⏳ مستنيين إذن الميك */}
          {micPerm && !listening && (
            <div style={{ padding: '6px 12px', background: '#FFFBEB', borderTop: '1px solid #FDE68A', fontSize: '11px', color: '#92400E', fontWeight: 'bold' }}>
              🔔 اسمح للميكروفون من الرسالة اللي ظهرت فوق جنب عنوان الموقع (اختار "Allow on every visit" عشان ميسألكش تاني)
            </div>
          )}

          {/* شريط التسجيل */}
          {listening && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: '#FEF2F2', borderTop: '1px solid #FECACA', fontSize: '11px', color: '#B91C1C', fontWeight: 'bold' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', animation: 'pulse 1s ease-in-out infinite' }} />
              🎤 بسجّل… اتكلم، ولما تخلّص اضغط الميك تاني
              <span style={{ marginInlineStart: 'auto' }}>{recTick}</span>
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '10px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '6px', background: 'white' }}>
            <input
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && !e.shiftKey && sendAi()}
              placeholder={listening ? (voiceCfg.stt_provider !== 'browser' ? '🎤 بسجّل… اضغط الميك تاني لما تخلّص' : '🎤 بسمعك… اتكلم') : t.askQ}
              disabled={aiLoading}
              style={{ flex: 1, padding: '9px 12px', border: '2px solid #E5E7EB', borderRadius: '10px', outline: 'none', fontSize: '12px', background: aiLoading ? '#F9FAFB' : 'white', transition: 'border-color .2s' }}
              onFocus={e => e.target.style.borderColor = '#667eea'}
              onBlur={e => e.target.style.borderColor = '#E5E7EB'}
            />
            <button onClick={toggleMic} disabled={aiLoading} title={listening ? 'إيقاف التسجيل' : 'اتكلم بالصوت'}
              style={{ padding: '9px 10px', background: listening ? '#ef4444' : micPerm ? '#F59E0B' : '#F3F4F6', color: (listening || micPerm) ? 'white' : '#4B5563', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', animation: listening ? 'pulse 1s ease-in-out infinite' : 'none' }}>
              {listening ? <MicOff size={15} /> : <Mic size={15} />}
            </button>
            <button onClick={() => sendAi()} disabled={aiLoading || !aiInput.trim()}
              style={{ padding: '9px 12px', background: aiLoading || !aiInput.trim() ? '#E5E7EB' : G1, color: aiLoading || !aiInput.trim() ? '#9CA3AF' : 'white', border: 'none', borderRadius: '10px', cursor: aiLoading || !aiInput.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', transition: 'all .2s' }}>
              {aiLoading ? <Loader size={15} style={SS} /> : <Send size={15} />}
            </button>
          </div>
        </div>
      )}

      {/* Bounce animation for typing indicator */}
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
        @keyframes wave {
          0%, 100% { transform: scaleY(0.5); }
          50% { transform: scaleY(1.3); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.12); }
        }
      `}</style>

      {/* WhatsApp button */}
      <button onClick={openWA}
        style={{ position: 'fixed', bottom: '22px', [side]: '22px', width: '52px', height: '52px', borderRadius: '50%', background: '#25D366', color: 'white', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,211,102,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 40, fontSize: '22px', transition: 'transform .2s' }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
        💬
      </button>
    </>
  );
}
